import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { register, googleAuth } from "../../services/auth";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import PhoneIcon from "@mui/icons-material/Phone";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

export default function RegisterPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    address: "",
    phone: "",
  });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }));
  };

  const handleGoogleRegister = async () => {
    try {
      setError("");
      setGoogleLoading(true);

      const res = await googleAuth();
      const url = res?.url;
      if (!url) throw new Error("Google auth url not returned");

      window.location.assign(url);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Google register failed");
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.email || !form.password || !form.name) {
      setError("يرجى ملء الحقول المطلوبة (email, password, name).");
      return;
    }

    setLoading(true);
    try {
      const res = await register({
        email: form.email,
        password: form.password,
        name: form.name,
        address: form.address,
        phone: form.phone,
      });

      const data = res?.data || res || {};
      const emailForVerify = data.email || data?.user?.email || form.email;
      const otpForVerify = data.otp || data.verification_code || data?.verification_code || "";
      const userIdForVerify = data?.user?.id || data?.data?.user?.id || data.user_id || data.id || "";

      navigate("/verify-account", {
        state: { email: emailForVerify, otp: otpForVerify, userId: userIdForVerify },
      });
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "حدث خطأ أثناء التسجيل.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        position: "relative",
        overflow: "hidden",
        py: 4,
        "&::before": {
          content: '""',
          position: "absolute",
          top: "-50%",
          right: "-20%",
          width: "80%",
          height: "80%",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(213, 170, 47, 0.08) 0%, transparent 70%)",
          animation: "float 20s ease-in-out infinite",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: "-50%",
          left: "-20%",
          width: "80%",
          height: "80%",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(213, 170, 47, 0.08) 0%, transparent 70%)",
          animation: "float 15s ease-in-out infinite reverse",
        },
        "@keyframes float": {
          "0%, 100%": { transform: "translate(0, 0)" },
          "50%": { transform: "translate(50px, 50px)" },
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            border: "1px solid",
            borderColor: "grey.100",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
            backdropFilter: "blur(10px)",
            animation: "fadeInUp 0.6s ease-out",
            "@keyframes fadeInUp": {
              from: { opacity: 0, transform: "translateY(30px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                mx: "auto",
                mb: 2,
                borderRadius: 3,
                background: "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 24px rgba(213, 170, 47, 0.3)",
              }}
            >
              <PersonAddIcon sx={{ fontSize: 40, color: "white" }} />
            </Box>
            <Typography
              variant="h4"
              fontWeight={800}
              sx={{
                mb: 1,
                background: "linear-gradient(135deg, #d5aa2f 0%, #b08c26 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              إنشاء حساب جديد
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              انضم إلى متجرنا الفاخر اليوم
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 3,
                animation: "shake 0.5s ease-in-out",
                "@keyframes shake": {
                  "0%, 100%": { transform: "translateX(0)" },
                  "25%": { transform: "translateX(-10px)" },
                  "75%": { transform: "translateX(10px)" },
                },
              }}
            >
              {error}
            </Alert>
          )}

          {/* Google Register Button */}
          <Button
            variant="outlined"
            size="large"
            onClick={handleGoogleRegister}
            disabled={googleLoading}
            startIcon={
              googleLoading ? (
                <CircularProgress size={20} />
              ) : (
                <img
                  src="https://developers.google.com/identity/images/g-logo.png"
                  alt="Google"
                  style={{ width: "20px", height: "20px" }}
                />
              )
            }
            sx={{
              py: 1.5,
              mb: 3,
              borderRadius: 3,
              textTransform: "none",
              fontWeight: 700,
              borderWidth: 2,
              "&:hover": {
                borderWidth: 2,
                bgcolor: "grey.50",
              },
            }}
            fullWidth
          >
            {googleLoading ? "جاري فتح Google..." : "التسجيل باستخدام Google"}
          </Button>

          {/* Divider */}
          <Divider sx={{ my: 3 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              أو
            </Typography>
          </Divider>

          {/* Register Form */}
          <form onSubmit={handleSubmit}>
            <Stack spacing={2.5}>
              <TextField
                label="الاسم الكامل"
                name="name"
                placeholder="أدخل اسمك الكامل"
                value={form.name}
                onChange={handleChange}
                required
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: "primary.main" }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                type="email"
                label="البريد الإلكتروني"
                name="email"
                placeholder="your@email.com"
                value={form.email}
                onChange={handleChange}
                required
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: "primary.main" }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                type={showPassword ? "text" : "password"}
                label="كلمة المرور"
                name="password"
                placeholder="••••••••"
                value={form.password}
                onChange={handleChange}
                required
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: "primary.main" }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="رقم الهاتف"
                name="phone"
                placeholder="+20 123 456 7890"
                value={form.phone}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon sx={{ color: "primary.main" }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="العنوان"
                name="address"
                placeholder="أدخل عنوانك"
                value={form.address}
                onChange={handleChange}
                multiline
                minRows={2}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1.5 }}>
                      <HomeIcon sx={{ color: "primary.main" }} />
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PersonAddIcon />}
                sx={{
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 800,
                  borderRadius: 3,
                  textTransform: "none",
                  background: "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
                  boxShadow: "0 4px 16px rgba(213, 170, 47, 0.35)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #b08c26 0%, #d5aa2f 100%)",
                    boxShadow: "0 6px 24px rgba(213, 170, 47, 0.45)",
                  },
                  "&:disabled": {
                    background: "linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)",
                  },
                }}
              >
                {loading ? "جاري إنشاء الحساب..." : "إنشاء حساب"}
              </Button>
            </Stack>
          </form>

          {/* Login Link */}
          <Box
            sx={{
              mt: 3,
              pt: 3,
              borderTop: "1px solid",
              borderColor: "grey.100",
              textAlign: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              لديك حساب بالفعل؟{" "}
              <Typography
                component="span"
                onClick={() => navigate("/login")}
                sx={{
                  color: "primary.main",
                  fontWeight: 700,
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                تسجيل الدخول
              </Typography>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
